import React, { useMemo, useEffect, useCallback, useState } from 'react';
import { PageLayout, PageLayoutConfig } from '@/components/layout/page-layout';
import { useStringsDataContext } from '@/providers/StringsDataProvider';
import { usePageConfig } from '@/hooks/usePageConfig';
import { useTMSStringUpdates } from '@/hooks/useTMSStringUpdates';
import { useOutletContext } from 'react-router-dom';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { usePageAccessGuard } from '@/hooks/usePageAccessGuard';

export default function TextManagementPage() {
  usePageAccessGuard('textManagement');
  const { isEditMode, setIsEditMode } = useOutletContext<{ isEditMode: boolean; setIsEditMode: (mode: boolean) => void }>();
  const { stringsItems } = useStringsDataContext();
  const { selectedGame } = useSimplifiedUserData();
  const { updateStringField } = useTMSStringUpdates();
  
  const [originalConfig, setOriginalConfig] = useState<PageLayoutConfig | null>(null);

  const defaultConfig: PageLayoutConfig = useMemo(() => ({
    title: "Text Management",
    spacing: 40,
    padding: 0,
    rows: [
      
    ]
  }), []);

  // Memoize the handlers to prevent infinite re-renders
  const handlers = useMemo(() => ({
    onCellUpdate: updateStringField,
  }), [updateStringField]);

  const onConfigUpdate = useCallback((config: PageLayoutConfig) => {
    setOriginalConfig(config);
  }, []);

  const {
    pageLayoutConfig,
    setPageLayoutConfig,
    isLoading,
    handlePageConfigChange,
    LoadingComponent,
    NoConfigComponent,
  } = usePageConfig({
    sectionKey: 'text_management',
    defaultConfig,
    title: 'Text Management',
    dependencies: [stringsItems, selectedGame],
    loadingMessage: 'Loading text management...',
    noConfigMessage: 'No text management configuration available',
    onConfigUpdate,
    handlers,
  });

  const handleSave = useCallback(async () => {
    if (pageLayoutConfig) {
      await handlePageConfigChange(pageLayoutConfig);
      setOriginalConfig(pageLayoutConfig);
    }
  }, [pageLayoutConfig, handlePageConfigChange]);

  const handleCancel = useCallback(() => {
    if (originalConfig) {
      setPageLayoutConfig(originalConfig);
    }
  }, [originalConfig, setPageLayoutConfig]);

  // Listen for events from AppLayout
  useEffect(() => {
    const handleSaveChanges = () => {
      handleSave();
    };

    const handleCancelChanges = () => {
      handleCancel();
    };

    window.addEventListener('saveChanges', handleSaveChanges);
    window.addEventListener('cancelChanges', handleCancelChanges);

    return () => {
      window.removeEventListener('saveChanges', handleSaveChanges);
      window.removeEventListener('cancelChanges', handleCancelChanges);
    };
  }, [handleSave, handleCancel]);

  if (isLoading) {
    return <LoadingComponent />;
  }

  if (!pageLayoutConfig) {
    return <NoConfigComponent />;
  }

  return (
    <div className="h-full flex flex-col">
      <PageLayout
        config={pageLayoutConfig}
        onConfigChange={handlePageConfigChange}
        editMode={isEditMode}
        onEditModeChange={setIsEditMode}
        showTitle={false}
      />
    </div>
  );
} 